/**
 * „Zeitraum bearbeiten" in der Werkzeugleiste — Apartment und Zeitraum frei waehlen, ohne erst
 * eine Zelle anzuklicken. Danach oeffnet sich derselbe Inline-Editor unter der Apartmentzeile;
 * es gibt keinen zweiten Bearbeitungsweg.
 */
import * as Popover from '@radix-ui/react-popover';
import { PencilLine } from 'lucide-react';
import { useId, useState } from 'react';
import { Button, Select } from '@/shared/components';
import type { AvailabilityDistributionApartment } from '@/shared/api';
import { RangeCalendar } from './RangeCalendar';
import type { IsoRange } from './rangePickerModel';

interface PeriodPickerButtonProps {
  apartments: AvailabilityDistributionApartment[];
  today: string;
  preferredPropertyId: string | null;
  onPick: (propertyId: string, range: IsoRange) => void;
}

export function PeriodPickerButton({
  apartments,
  today,
  preferredPropertyId,
  onPick,
}: PeriodPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const selectId = useId();

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (next) setPropertyId(preferredPropertyId ?? apartments[0]?.propertyId ?? '');
        setOpen(next);
      }}
    >
      <Popover.Trigger asChild>
        <Button
          variant="brand"
          className="rounded-pill h-11 px-[18px] text-[14px]"
          leftIcon={<PencilLine size={15} aria-hidden />}
        >
          Zeitraum bearbeiten
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          aria-label="Zeitraum bearbeiten"
          className="bg-card border-hairline shadow-panel rounded-tile z-50 max-h-[85dvh] w-[min(640px,calc(100vw-32px))] overflow-y-auto border p-5"
        >
          <p className="text-ink mb-3 font-serif text-[16px]">Zeitraum bearbeiten</p>
          {open && (
            <RangeCalendar
              today={today}
              applyLabel="Auswählen"
              canApply={propertyId !== ''}
              header={
                <div>
                  <label
                    htmlFor={selectId}
                    className="text-ink-soft mb-1.5 block text-[12px] font-medium"
                  >
                    Apartment
                  </label>
                  <Select
                    id={selectId}
                    value={propertyId}
                    onChange={(event) => setPropertyId(event.target.value)}
                    className="text-[14px]"
                  >
                    {apartments.map((apartment) => (
                      <option key={apartment.propertyId} value={apartment.propertyId}>
                        {apartment.name}
                      </option>
                    ))}
                  </Select>
                </div>
              }
              onCancel={() => setOpen(false)}
              onApply={(range) => {
                onPick(propertyId, range);
                setOpen(false);
              }}
            />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
